import { useTranslations } from "next-intl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	GEOLOCATION_MAX_PRECISION,
	GEOLOCATION_MIN_PRECISION,
	type GeolocationMode,
} from "@/lib/fields/geolocation";
import type { FieldSettingsProps } from "./types";

export function GeolocationFieldSettings({
	field,
	onUpdateSettings,
}: FieldSettingsProps) {
	const t = useTranslations("fieldSettings");
	const mode: GeolocationMode =
		(field.settings?.geolocationMode as GeolocationMode | undefined) ?? "both";
	const precision = field.settings?.geolocationPrecision ?? 5;

	return (
		<Card className="gap-2 p-4 shadow-none">
			<CardHeader className="p-0">
				<CardTitle className="flex items-center gap-2 text-lg">
					{t("geolocation.title")}
				</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col gap-4 p-0">
				<div className="flex flex-col gap-2">
					<Label className="font-medium text-sm" htmlFor="geolocation-mode">
						{t("geolocation.mode")}
					</Label>
					<Select
						onValueChange={(value: string) =>
							onUpdateSettings({
								geolocationMode: value as GeolocationMode,
							})
						}
						value={mode}
					>
						<SelectTrigger id="geolocation-mode">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="both">{t("geolocation.modeBoth")}</SelectItem>
							<SelectItem value="current">
								{t("geolocation.modeCurrent")}
							</SelectItem>
							<SelectItem value="map">{t("geolocation.modeMap")}</SelectItem>
						</SelectContent>
					</Select>
					<p className="text-muted-foreground text-xs">
						{t("geolocation.modeHelp")}
					</p>
				</div>

				<div className="flex flex-col gap-2">
					<Label
						className="font-medium text-sm"
						htmlFor="geolocation-precision"
					>
						{t("geolocation.precision")}
					</Label>
					<Input
						aria-describedby="geolocation-precision-help"
						autoComplete="off"
						id="geolocation-precision"
						max={GEOLOCATION_MAX_PRECISION}
						min={GEOLOCATION_MIN_PRECISION}
						name="geolocation-precision"
						onChange={(e) => {
							const parsed = Number.parseInt(e.target.value, 10);
							onUpdateSettings({
								geolocationPrecision: Number.isFinite(parsed)
									? parsed
									: undefined,
							});
						}}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								e.currentTarget.blur();
							}
						}}
						type="number"
						value={precision}
					/>
					<p
						className="text-muted-foreground text-xs"
						id="geolocation-precision-help"
					>
						{t("geolocation.precisionHelp")}
					</p>
				</div>

				<div className="flex flex-col gap-2">
					<Label className="font-medium text-sm" htmlFor="geolocation-message">
						{t("geolocation.customErrorMessage")}
					</Label>
					<Input
						aria-describedby="geolocation-message-help"
						autoComplete="off"
						id="geolocation-message"
						name="geolocation-message"
						onChange={(e) =>
							onUpdateSettings({ requiredMessage: e.target.value })
						}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								e.currentTarget.blur();
							}
						}}
						placeholder={t("geolocation.errorPlaceholder")}
						type="text"
						value={field.settings?.requiredMessage ?? ""}
					/>
					<p
						className="text-muted-foreground text-xs"
						id="geolocation-message-help"
					>
						{t("geolocation.errorHelp")}
					</p>
				</div>
			</CardContent>
		</Card>
	);
}
